import React, { useState, useEffect } from 'react';
import { ChevronDown, CheckCircle, Star, ArrowRight, Zap, Target, Users, Trophy, Sparkles, Play, User, Mail, Calendar, MousePointer } from 'lucide-react';

const App = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      setShowFloatingNav(window.scrollY > 100);
      
      // Scroll spy functionality
      const sections = ['section-0', 'section-1', 'section-2', 'section-3'];
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      sections.forEach((sectionId, index) => {
        const element = document.getElementById(sectionId);
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;
          
          if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
            setActiveSection(index);
          }
        }
      });
    };

    // Initial check
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-cycle through steps in How it Works section


  const handleStepClick = (index) => {
    setActiveStep(index);
    setIsAutoPlaying(false);
    
    // Resume auto-playing after 10 seconds
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 10000);
  };

  const scrollToSection = (index) => {
    setActiveSection(index);
    const element = document.getElementById(`section-${index}`);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    { icon: <User size={24} />, title: "LinkedIn Revamp", desc: "Fully redesign and rewrite your profile to stand out to recruiters", color: "from-cyan-400 to-blue-500" },
    { icon: <Mail size={24} />, title: "Resume Makeover", desc: "ATS-optimized, modern, and tailored to your industry", color: "from-purple-400 to-pink-500" },
    { icon: <Target size={24} />, title: "Job Applications", desc: "We apply to handpicked roles on your behalf, every week", color: "from-green-400 to-cyan-500" },
    { icon: <Calendar size={24} />, title: "Interview Setup", desc: "We schedule interviews and handle coordination", color: "from-orange-400 to-red-500" },
    { icon: <Users size={24} />, title: "Interview Coaching", desc: "Custom prep to help you feel confident and ready", color: "from-pink-400 to-purple-500" },
    { icon: <Sparkles size={24} />, title: "Regular Updates", desc: "Receive status reports and progress updates", color: "from-yellow-400 to-orange-500" }
  ];

  const steps = [
    { number: "01", title: "We Get To Know You", desc: "Learning about your goals, strengths, and dream roles", gradient: "from-cyan-500 to-blue-600" },
    { number: "02", title: "We Supercharge Your LinkedIn", desc: "Professional rewrite and optimization for maximum impact", gradient: "from-purple-500 to-pink-600" },
    { number: "03", title: "We Apply & Reach Out", desc: "Handling applications and direct outreach to hiring managers", gradient: "from-green-500 to-cyan-600" },
    { number: "04", title: "You Land The Right Job", desc: "Focus on interviews while we handle everything else", gradient: "from-orange-500 to-red-600" }
  ];
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 4000); // Change step every 4 seconds

    return () => clearInterval(interval);
  }, [steps.length, isAutoPlaying]);
  const testimonials = [
    { name: "Emily S.", text: "I had no idea my LinkedIn could look that good. I started getting interview requests in a week.", avatar: "👩‍💼", role: "Software Engineer" },
    { name: "Max K.", text: "They basically ran my job search for me. I just showed up to interviews.", avatar: "👨‍💻", role: "Product Manager" },
    { name: "Jordan D.", text: "Landed a remote role in two months. Worth every penny.", avatar: "🧑‍🎨", role: "UX Designer" },
    { name: "Sarah M.", text: "The personalized approach made all the difference. Got 3 offers!", avatar: "👩‍🚀", role: "Data Scientist" },
    { name: "Alex P.", text: "Finally broke into tech! The interview coaching was incredible.", avatar: "👨‍🔬", role: "Frontend Developer" },
    { name: "Maya R.", text: "From application to offer in 6 weeks. These guys are magic!", avatar: "👩‍🎓", role: "Marketing Manager" }
  ];

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Animated Background Elements */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 25%, #581c87 50%, #1e1b4b 75%, #0f172a 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 15s ease infinite',
        zIndex: -2
      }}>
        {/* Floating orbs */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '60%',
          right: '10%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite reverse'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '30%',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 10s ease-in-out infinite'
        }}></div>
      </div>

      {/* Floating Capsule Navigation */}
      <nav style={{
        position: 'fixed',
        top: showFloatingNav ? '20px' : '-100px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: showFloatingNav ? 1 : 0
      }}>
        <div style={{
          background: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(20px)',
          borderRadius: '50px',
          padding: '12px 24px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            paddingRight: '16px',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              background: 'linear-gradient(45deg, #06b6d4, #8b5cf6)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Zap size={14} color="white" />
            </div>
            <span style={{ 
              fontSize: '16px', 
              fontWeight: '600',
              background: 'linear-gradient(45deg, #06b6d4, #8b5cf6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>ZenHire</span>
          </div>
          
          {['Home', 'Pricing', 'How It Works', 'About'].map((item, index) => (
            <button
              key={item}
              onClick={() => scrollToSection(index)}
              style={{
                background: activeSection === index ? 'linear-gradient(45deg, #06b6d4, #8b5cf6)' : 'transparent',
                border: 'none',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '25px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                if (activeSection !== index) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== index) {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {item}
            </button>
          ))}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="section-0" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '80px 24px',
        position: 'relative'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
          <div style={{
            transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.9)',
            opacity: isVisible ? 1 : 0,
            transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            {/* Floating Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(45deg, rgba(6, 182, 212, 0.2), rgba(139, 92, 246, 0.2))',
              backdropFilter: 'blur(20px)',
              borderRadius: '50px',
              padding: '12px 24px',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              marginBottom: '48px',
              animation: 'float 3s ease-in-out infinite',
              boxShadow: '0 8px 32px rgba(6, 182, 212, 0.2)'
            }}>
              <Trophy size={20} color="#06b6d4" />
              <span style={{ color: '#06b6d4', fontWeight: '600' }}>Where hiring meets zen</span>
            </div>
            
            {/* Main Heading with Animated Gradient */}
            <h1 style={{
              fontSize: 'clamp(3rem, 8vw, 5rem)',
              fontWeight: '800',
              lineHeight: '1.1',
              marginBottom: '32px',
              position: 'relative'
            }}>
              Your job search,{' '}
              <span style={{
                background: 'linear-gradient(45deg, #06b6d4, #8b5cf6, #ec4899, #06b6d4)',
                backgroundSize: '300% 300%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradientShift 3s ease-in-out infinite'
              }}>
                done for you
              </span>
            </h1>
            
            {/* Subtitle */}
            <p style={{
              fontSize: '1.25rem',
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: '48px',
              maxWidth: '800px',
              margin: '0 auto 48px',
              lineHeight: '1.7'
            }}>
              We rewrite your LinkedIn, apply for the right roles, and connect you directly to hiring managers — 
              so you can focus on landing your next opportunity.
            </p>

            {/* CTA Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: window.innerWidth < 768 ? 'column' : 'row',
              gap: '24px',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '80px'
            }}>
              <button style={{
                background: 'linear-gradient(45deg, #06b6d4, #8b5cf6)',
                color: 'white',
                padding: '18px 40px',
                border: 'none',
                borderRadius: '15px',
                fontSize: '18px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px) scale(1.05)';
                e.target.style.boxShadow = '0 20px 40px rgba(139, 92, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 8px 32px rgba(139, 92, 246, 0.3)';
              }}>
                <MousePointer size={20} />
                Start Your Journey
              </button>
              
              <button style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                color: 'white',
                padding: '18px 40px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '15px',
                fontSize: '18px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.transform = 'translateY(0)';
              }}>
                <Play size={20} />
                Watch Demo
              </button>
            </div>
          </div>

          {/* Enhanced Features Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '24px',
            marginTop: '80px',
            padding: '0 20px'
          }}>
            {features.map((feature, index) => (
              <div
                key={index}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '24px',
                  padding: '32px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  opacity: isVisible ? 1 : 0,
                  transitionDelay: `${index * 100}ms`,
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-8px) scale(1.02)';
                  e.target.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                  e.target.style.boxShadow = '0 20px 40px rgba(139, 92, 246, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {/* Gradient overlay on hover */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${feature.color.split(' ')[1]} 0%, transparent 50%)`,
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none'
                }}></div>
                
                <div style={{
                  background: `linear-gradient(45deg, ${feature.color.split(' ')[1]}, ${feature.color.split(' ')[3]})`,
                  width: '64px',
                  height: '64px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  boxShadow: `0 8px 24px ${feature.color.split(' ')[1]}40`
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: 'white'
                }}>{feature.title}</h3>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: '1.6',
                  fontSize: '1rem'
                }}>{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Animated Scroll Indicator */}
          <button
            onClick={() => scrollToSection(1)}
            style={{
              marginTop: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              margin: '80px auto 0',
              animation: 'bounce 2s infinite',
              boxShadow: '0 8px 24px rgba(255, 255, 255, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <ChevronDown size={24} />
          </button>
        </div>
      </section>

      {/* Enhanced Pricing Section */}
      <section id="section-1" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px',
        position: 'relative'
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(2.5rem, 6vw, 4rem)',
            fontWeight: '800',
            marginBottom: '24px',
            color: 'white'
          }}>
            Simple,{' '}
            <span style={{
              background: 'linear-gradient(45deg, #10b981, #06b6d4)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              transparent
            </span>{' '}
            pricing
          </h2>
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '64px',
            maxWidth: '600px',
            margin: '0 auto 64px'
          }}>No hidden fees, no long-term contracts. Pay monthly, pause anytime.</p>

          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
            backdropFilter: 'blur(20px)',
            borderRadius: '32px',
            padding: '48px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxWidth: '600px',
            margin: '0 auto',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
          }}>
            {/* Animated background pattern */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
              animation: 'rotate 20s linear infinite'
            }}></div>
            
            <div style={{
              background: 'linear-gradient(45deg, #10b981, #06b6d4)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '50px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '32px',
              display: 'inline-block',
              animation: 'pulse 2s infinite'
            }}>
              Most Popular 🔥
            </div>
            
            <h3 style={{
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '24px',
              color: 'white'
            }}>Ghost LinkedIn Subscription</h3>
            
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              marginBottom: '32px'
            }}>
              <span style={{
                fontSize: '4rem',
                fontWeight: '800',
                color: '#10b981'
              }}>$28</span>
              <span style={{
                fontSize: '1.5rem',
                color: 'rgba(255, 255, 255, 0.6)',
                marginLeft: '8px'
              }}>/month</span>
            </div>
            
            <div style={{
              background: 'linear-gradient(45deg, rgba(251, 191, 36, 0.2), rgba(249, 115, 22, 0.2))',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '24px',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              marginBottom: '40px'
            }}>
              <p style={{
                fontSize: '1.1rem',
                color: 'white'
              }}>
                <span style={{ color: '#fbbf24', fontWeight: '700' }}>Success Fee:</span> Only 3% of your first year's salary when you land the job
              </p>
            </div>

            <div style={{ textAlign: 'left', marginBottom: '40px' }}>
              {features.slice(0, 4).map((feature, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  marginBottom: '16px',
                  padding: '12px',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                }}>
                  <CheckCircle size={24} color="#10b981" />
                  <span style={{ color: 'white' }}>{feature.title} - {feature.desc}</span>
                </div>
              ))}
            </div>

            <button style={{
                background: 'linear-gradient(45deg, #06b6d4, #8b5cf6)',
                color: 'white',
                padding: '18px 40px',
                border: 'none',
                borderRadius: '15px',
                fontSize: '18px',
                fontWeight: '600',
                display: 'inline-flex',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px) scale(1.05)';
                e.target.style.boxShadow = '0 20px 40px rgba(139, 92, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 8px 32px rgba(139, 92, 246, 0.3)';
              }}>
              Get Started Today 🚀
            </button>
          </div>
        </div>
      </section>

      {/* Enhanced How It Works Section */}
      <section id="section-2" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px',
        position: 'relative'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: '800',
              marginBottom: '24px',
              color: 'white',
              position: 'relative'
            }}>
              How it{' '}
              <span style={{
                background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                works
              </span>
              {isAutoPlaying && (
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '-20px',
                  background: 'linear-gradient(45deg, #10b981, #06b6d4)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  animation: 'pulse 2s infinite'
                }}>
                  Auto ▶
                </div>
              )}
            </h2>
            <p style={{
              fontSize: '1.25rem',
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>Four simple steps to your dream job</p>
          </div>

          {/* Step Tabs */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '60px',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {steps.map((step, index) => (
              <button
                key={index}
                onClick={() => handleStepClick(index)}
                style={{
                  background: activeStep === index 
                    ? 'linear-gradient(45deg, #ec4899, #8b5cf6)' 
                    : 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  border: activeStep === index 
                    ? 'none' 
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '16px 32px',
                  borderRadius: '50px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(20px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  if (activeStep !== index) {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeStep !== index) {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                <span style={{
                  background: activeStep === index 
                    ? 'rgba(255, 255, 255, 0.2)' 
                    : 'linear-gradient(45deg, #ec4899, #8b5cf6)',
                  color: 'white',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '700'
                }}>
                  {index + 1}
                </span>
                {step.title}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
            backdropFilter: 'blur(20px)',
            borderRadius: '32px',
            padding: '60px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            minHeight: '400px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
              gap: '60px',
              alignItems: 'center'
            }}>
              {/* Left Content */}
              <div>
                <div style={{
                  background: `linear-gradient(45deg, ${steps[activeStep].gradient.split(' ')[1]}, ${steps[activeStep].gradient.split(' ')[3]})`,
                  color: 'white',
                  width: '80px',
                  height: '80px',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  fontWeight: '800',
                  marginBottom: '32px',
                  boxShadow: `0 12px 32px ${steps[activeStep].gradient.split(' ')[1]}40`
                }}>
                  {steps[activeStep].number}
                </div>
                
                <h3 style={{
                  fontSize: '2.5rem',
                  fontWeight: '700',
                  color: 'white',
                  marginBottom: '24px',
                  lineHeight: '1.2'
                }}>
                  {steps[activeStep].title}
                </h3>
                
                <p style={{
                  fontSize: '1.3rem',
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.7',
                  marginBottom: '32px'
                }}>
                  {steps[activeStep].desc}
                </p>

                {/* Feature bullets for current step */}
                <div style={{ marginBottom: '32px' }}>
                  {activeStep === 0 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <CheckCircle size={20} color="#10b981" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Comprehensive career assessment</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <CheckCircle size={20} color="#10b981" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Industry preference mapping</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <CheckCircle size={20} color="#10b981" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Personalized job targeting</span>
                      </div>
                    </div>
                  )}
                  {activeStep === 1 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <CheckCircle size={20} color="#06b6d4" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Professional headline optimization</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <CheckCircle size={20} color="#06b6d4" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Experience section enhancement</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <CheckCircle size={20} color="#06b6d4" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Skills and endorsements boost</span>
                      </div>
                    </div>
                  )}
                  {activeStep === 2 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <CheckCircle size={20} color="#10b981" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Strategic job matching</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <CheckCircle size={20} color="#10b981" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Direct hiring manager outreach</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <CheckCircle size={20} color="#10b981" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Follow-up automation</span>
                      </div>
                    </div>
                  )}
                  {activeStep === 3 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <CheckCircle size={20} color="#f97316" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Interview preparation sessions</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <CheckCircle size={20} color="#f97316" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Salary negotiation guidance</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <CheckCircle size={20} color="#f97316" />
                        <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Offer evaluation support</span>
                      </div>
                    </div>
                  )}
                </div>

                <button style={{
                background: 'linear-gradient(45deg, #06b6d4, #8b5cf6)',
                color: 'white',
                padding: '18px 40px',
                border: 'none',
                borderRadius: '15px',
                fontSize: '18px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px) scale(1.05)';
                e.target.style.boxShadow = '0 20px 40px rgba(139, 92, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 8px 32px rgba(139, 92, 246, 0.3)';
              }}>
                  Learn More
                  <ArrowRight size={16} />
                </button>
              </div>

              {/* Right Image */}
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                  borderRadius: '24px',
                  padding: '40px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Mock Phone */}
                  <div style={{
                    width: '280px',
                    height: '500px',
                    background: 'linear-gradient(135deg, #1f2937, #374151)',
                    borderRadius: '32px',
                    padding: '8px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                    position: 'relative'
                  }}>
                    <div style={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, #0f172a, #1e1b4b)',
                      borderRadius: '24px',
                      padding: '24px',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Phone UI based on active step */}
                      {activeStep === 0 && (
                        <div>
                          <div style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '24px' }}>
                            Career Assessment
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                            <div style={{ color: '#06b6d4', fontSize: '14px', marginBottom: '8px' }}>What's your dream role?</div>
                            <div style={{ color: 'white', fontSize: '16px' }}>Software Engineer</div>
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                            <div style={{ color: '#06b6d4', fontSize: '14px', marginBottom: '8px' }}>Preferred industry?</div>
                            <div style={{ color: 'white', fontSize: '16px' }}>Tech Startups</div>
                          </div>
                          <div style={{ background: 'linear-gradient(45deg, #10b981, #06b6d4)', borderRadius: '12px', padding: '16px', color: 'white', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                            Continue Assessment
                          </div>
                        </div>
                      )}
                      {activeStep === 1 && (
                        <div>
                          <div style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '24px' }}>
                            LinkedIn Optimization
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                            <div style={{ color: '#8b5cf6', fontSize: '14px', marginBottom: '8px' }}>New Headline</div>
                            <div style={{ color: 'white', fontSize: '14px', lineHeight: '1.4' }}>🚀 Full-Stack Developer | React & Node.js Expert | Building Amazing User Experiences</div>
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                            <div style={{ color: '#8b5cf6', fontSize: '14px', marginBottom: '8px' }}>Profile Views</div>
                            <div style={{ color: '#10b981', fontSize: '20px', fontWeight: '600' }}>+245% ↗️</div>
                          </div>
                          <div style={{ background: 'linear-gradient(45deg, #8b5cf6, #ec4899)', borderRadius: '12px', padding: '16px', color: 'white', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                            View Optimized Profile
                          </div>
                        </div>
                      )}
                      {activeStep === 2 && (
                        <div>
                          <div style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '24px' }}>
                            Job Applications
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>Google</div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>Frontend Engineer</div>
                              </div>
                              <div style={{ background: '#10b981', color: 'white', padding: '4px 8px', borderRadius: '8px', fontSize: '12px' }}>Applied</div>
                            </div>
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>Stripe</div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>Full Stack Developer</div>
                              </div>
                              <div style={{ background: '#f59e0b', color: 'white', padding: '4px 8px', borderRadius: '8px', fontSize: '12px' }}>Interview</div>
                            </div>
                          </div>
                          <div style={{ color: '#06b6d4', fontSize: '14px', fontWeight: '600', textAlign: 'center' }}>
                            12 applications this week 🎯
                          </div>
                        </div>
                      )}
                      {activeStep === 3 && (
                        <div>
                          <div style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '24px' }}>
                            Job Offer! 🎉
                          </div>
                          <div style={{ background: 'linear-gradient(45deg, #10b981, #06b6d4)', borderRadius: '12px', padding: '20px', marginBottom: '16px', color: 'white' }}>
                            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Congratulations!</div>
                            <div style={{ fontSize: '14px', marginBottom: '12px' }}>You've received a job offer from TechCorp</div>
                            <div style={{ fontSize: '24px', fontWeight: '700' }}>$95,000/year</div>
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                            <div style={{ color: '#fbbf24', fontSize: '14px', marginBottom: '8px' }}>Our Success Fee</div>
                            <div style={{ color: 'white', fontSize: '16px', fontWeight: '600' }}>$2,850 (3% of salary)</div>
                          </div>
                          <div style={{ background: 'linear-gradient(45deg, #f97316, #ef4444)', borderRadius: '12px', padding: '16px', color: 'white', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                            Accept Offer
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Floating elements */}
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    background: 'linear-gradient(45deg, #06b6d4, #8b5cf6)',
                    borderRadius: '12px',
                    padding: '12px',
                    animation: 'float 3s ease-in-out infinite'
                  }}>
                    <Sparkles size={24} color="white" />
                  </div>
                  <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '20px',
                    background: 'linear-gradient(45deg, #ec4899, #f97316)',
                    borderRadius: '12px',
                    padding: '12px',
                    animation: 'float 4s ease-in-out infinite reverse'
                  }}>
                    <Trophy size={24} color="white" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
            marginTop: '40px'
          }}>
            <button
              onClick={() => handleStepClick(Math.max(0, activeStep - 1))}
              disabled={activeStep === 0}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: activeStep === 0 ? 'rgba(255, 255, 255, 0.3)' : 'white',
                padding: '12px 24px',
                borderRadius: '50px',
                cursor: activeStep === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              ← Previous
            </button>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              {steps.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: index === activeStep 
                      ? 'linear-gradient(45deg, #ec4899, #8b5cf6)' 
                      : 'rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                />
              ))}
            </div>

            <button
              onClick={() => handleStepClick(Math.min(steps.length - 1, activeStep + 1))}
              disabled={activeStep === steps.length - 1}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: activeStep === steps.length - 1 ? 'rgba(255, 255, 255, 0.3)' : 'white',
                padding: '12px 24px',
                borderRadius: '50px',
                cursor: activeStep === steps.length - 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              Next →
            </button>
          </div>
        </div>
      </section>

      {/* Enhanced About/Testimonials Section */}
      <section id="section-3" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px',
        position: 'relative'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: '800',
              marginBottom: '24px',
              color: 'white'
            }}>
              Our{' '}
              <span style={{
                background: 'linear-gradient(45deg, #f97316, #ef4444)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Story
              </span>
            </h2>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
            backdropFilter: 'blur(20px)',
            borderRadius: '32px',
            padding: '48px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            marginBottom: '64px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
          }}>
            <p style={{
              fontSize: '1.3rem',
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: '1.8',
              marginBottom: '32px'
            }}>
              We started this company because we saw how broken the job hunt can be — especially for Gen Z and young professionals. 
              Resumes pile up. LinkedIn messages get ignored. You're told to "network more" without any real guidance.
            </p>
            <p style={{
              fontSize: '1.3rem',
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: '1.8'
            }}>
              So we decided to flip the script. We built a service that does the hard work for you: crafting your story, 
              getting you in front of the right people, and keeping you encouraged every step of the way. 
              Because landing your dream job shouldn't feel like a full-time job.
            </p>
          </div>

          <div>
            <h3 style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '48px',
              color: 'white'
            }}>Clients Can't Gatekeep Us</h3>
            
            {/* Auto-scrolling testimonials */}
            <div style={{
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
              height: '200px'
            }}
            onMouseEnter={(e) => {
              const scrollDiv = e.currentTarget.querySelector('div > div');
              scrollDiv.style.animationPlayState = 'paused';
            }}
            onMouseLeave={(e) => {
              const scrollDiv = e.currentTarget.querySelector('div > div');
              scrollDiv.style.animationPlayState = 'running';
            }}>
              <div style={{
                display: 'flex',
                animation: 'scroll 40s linear infinite',
                gap: '24px',
                width: 'fit-content'
              }}>
                {/* Duplicate testimonials for seamless loop */}
                {[...testimonials, ...testimonials, ...testimonials].map((testimonial, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '24px',
                      padding: '32px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      minWidth: '350px',
                      maxWidth: '350px',
                      height: '180px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      flexShrink: 0
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        marginRight: '16px'
                      }}>{testimonial.avatar}</div>
                      <div>
                        <div style={{
                          fontWeight: '600',
                          color: 'white',
                          fontSize: '1.1rem'
                        }}>{testimonial.name}</div>
                        <div style={{
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: '0.9rem',
                          marginBottom: '8px'
                        }}>{testimonial.role}</div>
                        <div style={{
                          display: 'flex',
                          color: '#fbbf24'
                        }}>
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={14} fill="currentColor" />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontStyle: 'italic',
                      fontSize: '0.95rem',
                      lineHeight: '1.5',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>"{testimonial.text}"</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: '64px' }}>
            <button style={{
              background: 'linear-gradient(45deg, #f97316, #ef4444)',
              color: 'white',
              padding: '20px 48px',
              border: 'none',
              borderRadius: '16px',
              fontSize: '1.2rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 8px 32px rgba(249, 115, 22, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-4px) scale(1.05)';
              e.target.style.boxShadow = '0 20px 40px rgba(249, 115, 22, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 8px 32px rgba(249, 115, 22, 0.3)';
            }}>
              <span>Start Your Success Story</span>
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer style={{
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '64px 24px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(45deg, #06b6d4, #8b5cf6)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Zap size={24} color="white" />
            </div>
            <span style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              background: 'linear-gradient(45deg, #06b6d4, #8b5cf6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>ZenHire</span>
          </div>
          <p style={{
            color: 'rgba(255, 255, 255, 0.6)',
            marginBottom: '16px',
            fontSize: '1.1rem'
          }}>Where hiring meets zen</p>
          <p style={{
            color: 'rgba(255, 255, 255, 0.4)',
            fontSize: '0.9rem'
          }}>© 2025 ZenHire. Helping Gen Z land their dream jobs.</p>
        </div>
      </footer>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% { transform: translate3d(0,0,0); }
          40%, 43% { transform: translate3d(0, -30px, 0); }
          70% { transform: translate3d(0, -15px, 0); }
          90% { transform: translate3d(0, -4px, 0); }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
        
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default App;